import "bootstrap/dist/css/bootstrap.min.css";
import React, { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import "./styles/common.scss";
import { AuthProvider } from "./context/AuthContext";

import PublicLayout from "./common/PublicLayout";
import AdminLayout from "./common/AdminLayout";
import ProtectedRoute from "./common/ProtectedRoute";
import UserProtectedRoute from "./common/UserProtectedRoute";
import ScrollToTop from "./common/ScrollToTop";
import LoadingFallback from "./common/LoadingFallback";
import SitemapView from "./common/SitemapView";

// Static Imports for Main Public Pages
import HomePage from "./pages/Home/HomePage";
import SyndicatedReportsPage from "./pages/SyndicatedReports/SyndicatedReportsPage";
import IndiaDataCenter from "./pages/IndiaDataCenter/IndiaDataCenter";

// Lazy Loaded Pages
const SignInPage = lazy(() => import("./pages/Auth/SignInPage"));
const UserDashboardPage = lazy(
  () => import("./pages/UserDashboard/UserDashboardPage"),
);
const AdminLogin = lazy(() => import("./pages/Admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/Admin/AdminDashboard"));
const UploadReport = lazy(() => import("./pages/Admin/UploadReport"));
const AllReports = lazy(() => import("./pages/Admin/AllReports"));
const UserManagement = lazy(() => import("./pages/Admin/UserManagement"));
const CategoryManagement = lazy(
  () => import("./pages/Admin/CategoryManagement"),
);
const Inquiries = lazy(() => import("./pages/Admin/Inquiries"));
const UserDocuments = lazy(() => import("./pages/Admin/UserDocuments"));
const CheckoutPage = lazy(() => import("./pages/Checkout/CheckoutPage"));
const ContactPage = lazy(() => import("./pages/Contact/ContactPage"));
const CustomResearchPage = lazy(
  () => import("./pages/CustomResearch/CustomResearchPage"),
);
const ServiceDetailsPage = lazy(
  () => import("./pages/Services/ServiceDetailsPage"),
);
const ServicesPage = lazy(() => import("./pages/Services/ServicesPage"));
const AboutUsPage = lazy(() => import("./pages/About/AboutUsPage"));
const IndustriesMainPage = lazy(
  () => import("./pages/Industries/IndustriesPage"),
);
const IndustryPage = lazy(() => import("./pages/Industry/IndustryPage"));
const HowToOrderPage = lazy(() => import("./pages/HowToOrder/HowToOrderPage"));
const SitemapPage = lazy(() => import("./pages/Sitemap/SitemapPage"));
const DisclaimerPage = lazy(() => import("./pages/Legal/DisclaimerPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/Legal/PrivacyPolicyPage"));
const CookiesPage = lazy(() => import("./pages/Legal/CookiesPage"));

function App() {
  return (
    <AuthProvider>
      <ScrollToTop />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Dynamic XML Sitemaps */}
          <Route path="/sitemap.xml" element={<SitemapView />} />
          <Route path="/sitemap/:name.xml" element={<SitemapView />} />

          {/* Public Routes with Header & Footer */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/signin" element={<SignInPage />} />
            <Route path="/about-us" element={<AboutUsPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route
              path="/services/:serviceSlug"
              element={<ServiceDetailsPage />}
            />
            <Route path="/how-to-order" element={<HowToOrderPage />} />
            <Route path="/sitemap" element={<SitemapPage />} />
            <Route path="/contact-us" element={<ContactPage />} />
            <Route path="/disclaimer" element={<DisclaimerPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/cookies" element={<CookiesPage />} />

            {/* Public Reports & Industry browsing routes (accessible without login) */}
            <Route path="/reports" element={<SyndicatedReportsPage />} />
            <Route path="/industries" element={<IndustriesMainPage />} />
            <Route
              path="/industries/:industrySlug"
              element={<IndustryPage />}
            />
            <Route path="/reports/:slug" element={<IndiaDataCenter />} />
            <Route path="/:industrySlug" element={<SyndicatedReportsPage />} />

            {/* Gated End-User Protected Routes (Redirects to /signin if unauthenticated) */}
            <Route element={<UserProtectedRoute />}>
              <Route path="/dashboard" element={<UserDashboardPage />} />
              <Route path="/workspace" element={<UserDashboardPage />} />
              <Route path="/purchase/:id" element={<CheckoutPage />} />
              <Route
                path="/request-custom-research"
                element={<CustomResearchPage />}
              />
            </Route>
          </Route>

          {/* Admin Routes with Sidebar & Header - Protected */}
          <Route element={<ProtectedRoute />}>
            <Route path="/admin" element={<AdminLayout />}>
              <Route
                index
                element={<Navigate to="/admin/dashboard" replace />}
              />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="reports" element={<AllReports />} />
              <Route path="reports/published" element={<AllReports />} />
              <Route path="reports/drafts" element={<AllReports />} />
              <Route path="reports/upload" element={<UploadReport />} />
              <Route path="reports/edit/:id" element={<UploadReport />} />

              {/* Super Admin Only Routes */}
              <Route
                element={<ProtectedRoute requiredRole="ROLE_SUPER_ADMIN" />}
              >
                <Route path="users" element={<UserManagement />} />
                <Route path="user-documents" element={<UserDocuments />} />
                <Route path="categories" element={<CategoryManagement />} />
                <Route path="inquiries" element={<Inquiries />} />
                <Route path="settings" element={<UserManagement />} />
              </Route>
            </Route>
          </Route>

          {/* Admin Auth Routes (No Sidebar) */}
          <Route path="/admin/login" element={<AdminLogin />} />
          {/* <Route path="/admin/signup" element={<AdminSignup />} /> */}
        </Routes>
      </Suspense>
    </AuthProvider>
  );
}

export default App;
